// src/pages/orders/PhoneBlackList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  PhoneOff,
  Plus,
  Edit,
  Trash2,
  X,
  FileText,
  Check,
  Loader,
} from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import PhoneNumberInput from '../../components/ui/PhoneNumberInput/PhoneNumberInput';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import {
  getPhoneBlacklist,
  addPhoneBlacklist,
  updatePhoneBlacklist,
  deletePhoneBlacklist,
} from '../../services/api/phoneBlacklist';
import { formatDate } from '../../utils/dateUtils';
import styles from './PhoneBlackList.module.css';

// ─── Add / Edit Modal ─────────────────────────────────────────────────────────

const BlacklistModal = ({ mode, record, onClose, onSuccess, t }) => {
  const { error: showError } = useAlert();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ phoneNumber: '', reason: '' });
  const [phoneError, setPhoneError] = useState('');
  const [phoneHasError, setPhoneHasError] = useState(false);

  useEffect(() => {
    if (mode === 'edit' && record) {
      setFormData({
        phoneNumber: record.phoneNumber || '',
        reason: record.reason || '',
      });
    }
  }, [mode, record]);

  const handlePhoneChange = (fullNumber) => {
    setFormData(prev => ({ ...prev, phoneNumber: fullNumber }));
    if (phoneError) setPhoneError('');
  };

  const handlePhoneValidation = (hasError, errorMessage) => {
    setPhoneHasError(hasError);
    if (hasError) setPhoneError(errorMessage);
    else setPhoneError('');
  };

  const validate = () => {
    if (!formData.phoneNumber) {
      setPhoneError(t('phoneBlacklist.phoneNumberRequired'));
      return false;
    }
    if (phoneHasError) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        phoneNumber: formData.phoneNumber,
        reason: formData.reason.trim() || null,
      };

      if (mode === 'edit') {
        await updatePhoneBlacklist(record.id, payload);
      } else {
        await addPhoneBlacklist(payload);
      }

      onSuccess();
    } catch (err) {
      showError(err.response?.data?.message || (mode === 'edit' ? t('phoneBlacklist.updateError') : t('phoneBlacklist.addError')));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>{mode === 'edit' ? t('phoneBlacklist.editEntry') : t('phoneBlacklist.addEntry')}</h2>
          <button onClick={onClose} className={styles.closeButton} disabled={isSubmitting}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalBody}>
          {/* Phone Number */}
          <div className={styles.formGroup}>
            <label htmlFor="blacklistPhoneNumber">
              {t('phoneBlacklist.phoneNumber')} *
            </label>
            <PhoneNumberInput
              id="blacklistPhoneNumber"
              value={formData.phoneNumber}
              onChange={handlePhoneChange}
              onValidationChange={handlePhoneValidation}
              placeholder={t('phoneBlacklist.phoneNumberPlaceholder')}
              error={phoneError}
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Reason */}
          <div className={styles.formGroup}>
            <label htmlFor="blacklistReason">
              {t('phoneBlacklist.reason')}
            </label>
            <div className={styles.inputWrapper}>
              <FileText className={styles.inputIcon} size={18} />
              <textarea
                id="blacklistReason"
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                placeholder={t('phoneBlacklist.reasonPlaceholder')}
                rows={3}
                disabled={isSubmitting}
              />
            </div>
          </div>
        </form>

        <div className={styles.modalFooter}>
          <button
            type="button"
            onClick={onClose}
            className={styles.cancelButton}
            disabled={isSubmitting}
          >
            {t('phoneBlacklist.cancel')}
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className={styles.submitButton}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader className={styles.buttonSpinner} size={18} />
                {t('common.saving')}
              </>
            ) : (
              <>
                <Check size={18} />
                {t('phoneBlacklist.save')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const PhoneBlackList = () => {
  const { t } = useLocalization();
  const { success, error: showError, confirm } = useAlert();
  const { hasPermission } = usePermissions();

  const canManage = hasPermission('manage_phone_blacklist');

  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [10, 20, 50, 100],
  });

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedRecord, setSelectedRecord] = useState(null);

  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getPhoneBlacklist({
        page: pagination.currentPage,
        limit: pagination.pageSize,
        search: search || undefined,
      });
      setRecords(response.records || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (err) {
      showError(err.response?.data?.message || t('phoneBlacklist.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [pagination.currentPage, pagination.pageSize, search]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleSearchChange = (value) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
  };

  const handleAdd = () => {
    setModalMode('create');
    setSelectedRecord(null);
    setShowModal(true);
  };

  const handleEdit = (record) => {
    setModalMode('edit');
    setSelectedRecord(record);
    setShowModal(true);
  };

  const handleDelete = async (record) => {
    const confirmed = await confirm(
      t('phoneBlacklist.confirmDeleteMessage', { phoneNumber: record.phoneNumber }),
      {
        confirmText: t('phoneBlacklist.delete'),
        title: t('phoneBlacklist.confirmDelete'),
      }
    );
    if (!confirmed) return;

    try {
      await deletePhoneBlacklist(record.id);
      success(t('phoneBlacklist.deletedSuccess'));
      loadRecords();
    } catch (err) {
      showError(err.response?.data?.message || t('phoneBlacklist.deleteError'));
    }
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setSelectedRecord(null);
    success(modalMode === 'edit' ? t('phoneBlacklist.updatedSuccess') : t('phoneBlacklist.addedSuccess'));
    loadRecords();
  };

  const handleExportCsv = () => {
    const headers = [
      t('phoneBlacklist.colPhoneNumber'),
      t('phoneBlacklist.colReason'),
      t('phoneBlacklist.colAddedBy'),
      t('phoneBlacklist.colAddedAt'),
    ];

    const rows = records.map(r => [
      r.phoneNumber,
      r.reason || '',
      r.addedBy?.name || '',
      formatDate(r.createdAt),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `phone-blacklist-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Table columns
  const columns = [
    {
      key: 'phoneNumber',
      header: t('phoneBlacklist.colPhoneNumber'),
      width: '180px',
      render: (row) => (
        <span className={styles.phoneNumber}>{row.phoneNumber}</span>
      ),
    },
    {
      key: 'reason',
      header: t('phoneBlacklist.colReason'),
      render: (row) => (
        <span className={styles.reason}>{row.reason || '—'}</span>
      ),
    },
    {
      key: 'addedBy',
      header: t('phoneBlacklist.colAddedBy'),
      width: '160px',
      render: (row) => (
        <span className={styles.addedBy}>{row.addedBy?.name || '—'}</span>
      ),
    },
    {
      key: 'createdAt',
      header: t('phoneBlacklist.colAddedAt'),
      width: '140px',
      render: (row) => (
        <span className={styles.addedAt}>{formatDate(row.createdAt)}</span>
      ),
    },
  ];

  const tableActions = canManage
    ? {
        render: (row) => (
          <div className={styles.actionButtons}>
            <button
              onClick={(e) => { e.stopPropagation(); handleEdit(row); }}
              className={styles.actionButton}
              title={t('phoneBlacklist.edit')}
            >
              <Edit size={16} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
              className={`${styles.actionButton} ${styles.deleteAction}`}
              title={t('phoneBlacklist.delete')}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ),
        width: '100px',
        frozen: true,
      }
    : undefined;

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('phoneBlacklist.pageTitle')}
        description={t('phoneBlacklist.pageDescription')}
        icon={PhoneOff}
        actions={
          canManage && (
            <button onClick={handleAdd} className={styles.primaryButton}>
              <Plus size={20} />
              {t('phoneBlacklist.addEntry')}
            </button>
          )
        }
      />

      <FilterPanel
        filters={[]}
        values={{}}
        onChange={() => {}}
        onClear={() => {}}
        show={false}
        onToggle={() => {}}
        onExport={handleExportCsv}
        showExport={true}
        exportButtonText={t('phoneBlacklist.exportCsv')}
        searchConfig={{
          show: true,
          value: search,
          onChange: handleSearchChange,
          placeholder: t('phoneBlacklist.searchPlaceholder'),
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions,
        }}
      />

      <DataTable
        data={records}
        columns={columns}
        actions={tableActions}
        onRowClick={canManage ? (row) => handleEdit(row) : undefined}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={t('phoneBlacklist.noRecords')}
        emptyDescription={t('phoneBlacklist.noRecordsDescription')}
        emptyIcon={<PhoneOff size={64} />}
        emptyAction={
          canManage && (
            <button onClick={handleAdd} className={styles.primaryButton}>
              <Plus size={18} />
              {t('phoneBlacklist.addEntry')}
            </button>
          )
        }
      />

      {showModal && (
        <BlacklistModal
          mode={modalMode}
          record={selectedRecord}
          onClose={() => { setShowModal(false); setSelectedRecord(null); }}
          onSuccess={handleModalSuccess}
          t={t}
        />
      )}
    </div>
  );
};

export default PhoneBlackList;
